import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface SocketEvent {
  type: string;
  data: any;
  timestamp: Date;
}

export interface OnlineStatusInfo {
  userId: string;
  isOnline: boolean;
  timestamp: string;
}

export interface MessageReadInfo {
  conversationId: string;
  messageIds: string[];
  readBy: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private connectionState$ = new BehaviorSubject<
    'disconnected' | 'connecting' | 'connected'
  >('disconnected');
  private destroy$ = new Subject<void>();

  private messageEvents$ = new Subject<SocketEvent>();
  private conversationEvents$ = new Subject<SocketEvent>();
  private onlineStatusEvents$ = new Subject<OnlineStatusInfo>();
  private unreadCountEvents$ = new Subject<void>();
  private messageReadEvents$ = new Subject<MessageReadInfo>();

  constructor(private authService: AuthService) {
    this.initializeConnection();
  }

  private initializeConnection(): void {
    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn && !this.socket?.connected) {
          this.connect();
        } else if (!isLoggedIn) {
          this.disconnect();
        }
      });
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cleanup(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      return;
    }

    this.connectionState$.next('connecting');

    try {
      const socketUrl = environment.websocketUrl || 'http://localhost:3000';

      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: false,
        forceNew: true,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.handleReconnection();
    }
  }

  disconnect(): void {
    this.cleanup();
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionState$.next('disconnected');
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.connectionState$.next('connected');
      this.startHeartbeat();
    });

    this.socket.on('disconnect', (reason) => {
      this.connectionState$.next('disconnected');
      this.stopHeartbeat();

      if (reason !== 'io client disconnect') {
        this.handleReconnection();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error.message);
      this.connectionState$.next('disconnected');
      this.handleReconnection();
    });

    this.socket.on('authenticated', () => {});

    this.socket.on('authentication_error', () => {
      this.disconnect();
    });

    this.socket.on('new_message', (data) => {
      this.messageEvents$.next({
        type: 'new_message',
        data,
        timestamp: new Date(),
      });
    });

    this.socket.on('message_updated', (data) => {
      this.messageEvents$.next({
        type: 'message_updated',
        data,
        timestamp: new Date(),
      });
    });

    this.socket.on('message_deleted', (data) => {
      this.messageEvents$.next({
        type: 'message_deleted',
        data,
        timestamp: new Date(),
      });
    });

    this.socket.on('messages_read', (data: MessageReadInfo) => {
      this.messageReadEvents$.next(data);
    });

    this.socket.on('conversation_updated', (data) => {
      this.conversationEvents$.next({
        type: 'conversation_updated',
        data,
        timestamp: new Date(),
      });
    });

    this.socket.on('conversation_deleted', (data) => {
      this.conversationEvents$.next({
        type: 'conversation_deleted',
        data,
        timestamp: new Date(),
      });
    });

    this.socket.on('user_online_status', (data: OnlineStatusInfo) => {
      this.onlineStatusEvents$.next(data);
    });

    this.socket.on('unread_count_updated', () => {
      this.unreadCountEvents$.next();
    });

    this.socket.on('pong', () => {});
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping');
      }
    }, 25000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private handleReconnection(): void {
    if (this.reconnectTimer) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    if (!this.authService.isLoggedIn()) return;

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );

    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.socket?.connected && this.authService.isLoggedIn()) {
        this.connect();
      }
    }, delay);
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getConnectionState(): Observable<
    'disconnected' | 'connecting' | 'connected'
  > {
    return this.connectionState$.asObservable();
  }

  getCurrentConnectionState(): 'disconnected' | 'connecting' | 'connected' {
    return this.connectionState$.value;
  }

  joinConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('join_conversation', { conversationId });
    }
  }

  leaveConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('leave_conversation', { conversationId });
    }
  }

  markAsRead(conversationId: string, messageIds: string[]): void {
    if (this.socket?.connected && messageIds.length > 0) {
      this.socket.emit('message_read', { conversationId, messageIds });
    }
  }

  getMessageEvents(): Observable<SocketEvent> {
    return this.messageEvents$.asObservable();
  }

  getConversationEvents(): Observable<SocketEvent> {
    return this.conversationEvents$.asObservable();
  }

  getOnlineStatusEvents(): Observable<OnlineStatusInfo> {
    return this.onlineStatusEvents$.asObservable();
  }

  getUnreadCountEvents(): Observable<void> {
    return this.unreadCountEvents$.asObservable();
  }

  getMessageReadEvents(): Observable<MessageReadInfo> {
    return this.messageReadEvents$.asObservable();
  }

  retry(): void {
    this.reconnectAttempts = 0;
    this.cleanup();
    this.disconnect();
    setTimeout(() => this.connect(), 500);
  }

  getStats(): any {
    return {
      connected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      connectionState: this.connectionState$.value,
      socketId: this.socket?.id,
    };
  }
}
